import React from "react";
import {
  UseFormRegister,
  FieldErrors,
  UseFormClearErrors,
} from "react-hook-form";
import StatesDropDown from "../../components/StatesDropDownMenu";
import MyGoogleMap from "../../components/MyGoogleMap";

interface AddressInfo {
  address: string;
  cityName: string;
  state: string;
  zipCode: string;
  latitude: number;
  longitude: number;
}
interface LocationDetails {
  address: string;
  cityName: string;
  state: string;
  zipCode: string;
  lat: number;
  lng: number;
}

interface AddressInfoProps {
  register: UseFormRegister<AddressInfo>;
  errors: FieldErrors<AddressInfo>;
  clearErrors: UseFormClearErrors<AddressInfo>;
  onStateChange: (state: string) => void;
  setValue: any;
  selectedState: string;
  hideAddress?: boolean;
  onInputChange: (field: string, value: string) => void;
  addressInfo: {
    address: string;
    cityName: string;
    state: string;
    zipCode: string;
    latitude: number;
    longitude: number;
  };
}

const BusinessAddressInformation: React.FC<AddressInfoProps> = ({
  register,
  errors,
  clearErrors,
  onStateChange,
  setValue,
  selectedState,
  hideAddress = false,
  onInputChange,
  addressInfo,
}) => {
  const handleInputChange = (fieldPath: string) => {
    clearErrors(fieldPath as keyof AddressInfo);
  };
  const handleOnLocationSelect = (details: LocationDetails) => {
    setValue("address", details.address);
    setValue("cityName", details.cityName);
    setValue("state", details.state);
    setValue("zipCode", details.zipCode);
    setValue("latitude", details.lat);
    setValue("longitude", details.lng);
  };

  const handleStateChange = (selectedState: string) => {
    clearErrors("state");
    setValue("state", selectedState);
    onStateChange(selectedState);
    handleInputChange("state");
  };

  const handleChange = (field: string, value: string) => {
    handleInputChange(field);
    onInputChange(field, value);
    setValue(field, value);
  };

  return (
    <div className="xxl:space-y-6 space-y-4">
      <div className="md:hidden w-full h-[247px] mb-6">
        <div className="w-full h-full flex flex-col items-center justify-center border rounded-lg">
          <div className="w-full h-full rounded-lg">
            <MyGoogleMap onLocationSelect={handleOnLocationSelect} />
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 grid-cols-1 gap-4">
        <div className="col-span-2">
          <label className="block xxl:text-sm text-xs font-semibold text-customGrayDark">
            Address<span className="text-red-500">*</span>
          </label>
          <input
            value={addressInfo.address}
            autoComplete="new-password"
            className={`mt-1 block w-full px-2 xxl:py-3 py-2 border ${
              errors?.address ? "border-red-500" : ""
            } border-gray-300 rounded-lg`}
            {...register("address", {
              required: "This field is required",
            })}
            onChange={(e) => handleChange("address", e.target.value)}
          />
          {errors?.address && (
            <p className="text-red-600 text-sm">{errors?.address?.message}</p>
          )}
        </div>

        <div className="col-span-2 md:pr-4 pr-0">
          <label className="block xxl:text-sm text-xs font-semibold text-customGrayDark">
            City<span className="text-red-500">*</span>
          </label>
          <input
            value={addressInfo.cityName}
            autoComplete="new-password"
            className={`mt-1 block md:w-1/2 w-full px-2 xxl:py-3 py-2 border ${
              errors?.cityName ? "border-red-500" : ""
            } border-gray-300 focus:border-customGreenColor focus:outline-customGreenColor rounded-lg`}
            {...register("cityName", {
              required: "This field is required",
            })}
            onChange={(e) => handleChange("cityName", e.target.value)}
          />
          {errors?.cityName && (
            <p className="text-red-600 text-sm">{errors?.cityName?.message}</p>
          )}
        </div>

        <div className="md:col-span-1 col-span-2">
          <label
            htmlFor="state"
            className="block mb-1 xxl:text-sm text-xs font-semibold text-customGrayDark"
          >
            State<span className="text-red-500">*</span>
          </label>
          <input
            value={addressInfo.state}
            autoComplete="new-password"
            className={`mt-1 block w-full px-2 xxl:py-3 py-2 border ${
              errors?.zipCode ? "border-red-500" : ""
            } border-gray-300 focus:border-customGreenColor focus:outline-customGreenColor rounded-lg`}
            {...register("state", {
              required: "This field is required",
            })}
            onChange={(e) => handleChange("state", e.target.value)}
          />
          {errors?.state && (
            <p className="text-red-600 text-sm">{errors?.state.message}</p>
          )}
        </div>

        <div className="md:col-span-1 col-span-2">
          <label className="block xxl:text-sm text-xs font-semibold text-customGrayDark">
            Zip Code<span className="text-red-500">*</span>
          </label>
          <input
            value={addressInfo.zipCode}
            autoComplete="new-password"
            className={`mt-1 block w-full px-2 xxl:py-3 py-2 border ${
              errors?.zipCode ? "border-red-500" : ""
            } border-gray-300 focus:border-customGreenColor focus:outline-customGreenColor rounded-lg`}
            {...register("zipCode", {
              required: "This field is required",
            })}
            onChange={(e) => handleChange("zipCode", e.target.value)}
          />
          {errors?.zipCode && (
            <p className="text-red-600 text-sm">{errors?.zipCode?.message}</p>
          )}
        </div>

        <div className="md:col-span-1 col-span-2">
          <label className="block xxl:text-sm text-xs font-semibold text-customGrayDark">
            Latitude<span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={addressInfo.latitude}
            autoComplete="new-password"
            className={`mt-1 block w-full px-2 xxl:py-3 py-2 border ${
              errors?.latitude ? "border-red-500" : ""
            } border-gray-300 focus:border-customGreenColor focus:outline-customGreenColor rounded-lg`}
            {...register("latitude", {
              required: "This field is required",
              pattern: {
                value: /^-?\d*\.?\d*$/,
                message: "Please enter a valid number",
              },
              onChange: (e) => {
                const value = e.target.value;
                if (value === "" || /^-?\d*\.?\d*$/.test(value)) {
                  handleChange("latitude", value);
                }
              },
            })}
          />
          {errors?.latitude && (
            <p className="text-red-600 text-sm">{errors?.latitude?.message}</p>
          )}
        </div>

        <div className="md:col-span-1 col-span-2">
          <label className="block xxl:text-sm text-xs font-semibold text-customGrayDark">
            Longitude<span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={addressInfo.longitude}
            autoComplete="new-password"
            className={`mt-1 block w-full px-2 xxl:py-3 py-2 border ${
              errors?.longitude ? "border-red-500" : ""
            } border-gray-300 focus:border-customGreenColor focus:outline-customGreenColor rounded-lg`}
            {...register("longitude", {
              required: "This field is required",
              pattern: {
                value: /^-?\d*\.?\d*$/,
                message: "Please enter a valid number",
              },
              onChange: (e) => {
                const value = e.target.value;
                if (value === "" || /^-?\d*\.?\d*$/.test(value)) {
                  handleChange("longitude", value);
                }
              },
            })}
          />
          {errors?.longitude && (
            <p className="text-red-600 text-sm">{errors?.longitude?.message}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BusinessAddressInformation;
